import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import style from './Footer.module.scss'
import commonStyle from '../../style.module.scss'
import krogerLogoBlue from '../assets/images/logo-blue.svg'
import appStoreBadge from '../assets/images/appstore-logo.svg'
import googlePlayBadge from '../assets/images/googleplay-logo.svg'
import fbIcon from '../assets/images/fb-logo.svg'
import xIcon from '../assets/images/x-logo.svg'
import igIcon from '../assets/images/ig-logo.svg'
import ytIcon from '../assets/images/yt-logo.svg'
import ptIcon from '../assets/images/pt-logo.svg'

interface IFooterProps {
  findability: boolean
  showCart: boolean
  showMain: boolean
  total: number
  onHomeClick: () => void
  onCartOpen: () => void
  showHint: () => void
  closeSimulation: () => void
}

const COLUMNS = [
  {
    title: 'colAboutUs',
    links: [
      'linkAboutCompany',
      'linkShopStoreBrands',
      'linkZeroHunger',
      'linkCommunityRewards',
      'linkCareers',
      'linkRequestDonation',
      'linkVendors',
      'linkNewsroom'
    ]
  },
  {
    title: 'colHelp',
    links: [
      'linkCustomerService',
      'linkFaqs',
      'linkFindStore',
      'linkFindPharmacy',
      'linkFreshness',
      'linkPrivacyCenter',
      'linkRecallAlerts'
    ]
  },
  {
    title: 'colLearnMore',
    links: [
      'linkLoyaltyCard',
      'linkBoostMembership',
      'linkPointsProgram',
      'linkGroceryPickup',
      'linkDeliveryProviders',
      'linkDeliveryLocation',
      'linkCustomCakes',
      'linkPartyPlatters'
    ]
  },
  {
    title: 'colServices',
    links: ['linkPharmacyHealth', 'linkHealthcareAppointments', 'linkMoneyServices', 'linkGiftCards', 'linkSnapEbt']
  },
  {
    title: 'colQuickLinks',
    links: [
      'linkShopAllDeals',
      'linkClipCoupons',
      'linkShopWeeklyAd',
      'linkStartYourOrder',
      'linkShopRecipes',
      'linkEasyMeals',
      'linkBrowseBlog',
      'linkScheduleAppointment'
    ]
  }
]

const LEGAL_LINKS = [
  'legalFinancialPrivacy',
  'legalHipaa',
  'legalPrivacy',
  'legalTerms',
  'legalTlcTerms',
  'legalTlcBillOfRights',
  'legalCaliforniaDietitian',
  'legalTelemedicineConsent'
]

const SOCIAL = [
  { src: fbIcon, key: 'socialFacebook' },
  { src: xIcon, key: 'socialX' },
  { src: ytIcon, key: 'socialYouTube' },
  { src: ptIcon, key: 'socialPinterest' },
  { src: igIcon, key: 'socialInstagram' }
]

const Footer = ({
  findability,
  showCart,
  showMain,
  total,
  onHomeClick,
  onCartOpen,
  showHint,
  closeSimulation
}: IFooterProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()

  if (viewport.mobile) {
    return (
      <footer className={style.footerMobile} data-testid="SW-EC-Footer">
        <button
          type="button"
          className={`${style.footerItem} ${showMain ? style.footerItemActive : ''}`}
          onClick={onHomeClick}
        >
          <span className={style.navIconWrap}>
            <span className={`${style.navIcon} ${style.shopIcon}`} />
          </span>
          <span className={style.footerItemText}>{t('questions', 'ec', 'kroger', 'navShop')}</span>
        </button>
        <EmptyLink color="#0e51a1">
          <div className={style.footerItem}>
            <span className={style.navIconWrap}>
              <span className={`${style.navIcon} ${style.saveIcon}`} />
            </span>
            <span className={style.footerItemText}>{t('questions', 'ec', 'kroger', 'navSave')}</span>
          </div>
        </EmptyLink>
        {findability ? (
          <EmptyLink color="#0e51a1">
            <div className={style.footerItem}>
              <span className={style.navIconWrap}>
                <span className={`${style.navIcon} ${style.cartIcon}`} />
              </span>
              <span className={style.footerItemText}>{t('questions', 'ec', 'kroger', 'cart')}</span>
            </div>
          </EmptyLink>
        ) : (
          <button
            type="button"
            className={`${style.footerItem} ${showCart ? style.footerItemActive : ''}`}
            onClick={onCartOpen}
          >
            <span className={style.navIconWrap}>
              <span className={`${style.navIcon} ${style.cartIcon}`} />
              {total > 0 && <span className={style.counter}>{total > 99 ? '99+' : total}</span>}
            </span>
            <span className={style.footerItemText}>{t('questions', 'ec', 'kroger', 'cart')}</span>
          </button>
        )}
        <div className={style.footerItem}>
          <div className={`${commonStyle.IconAytmPlace} ${style.helperIcon}`}>
            <i className="icon-aytmfd-help" onClick={showHint} />
          </div>
        </div>
        <div className={style.footerItem}>
          <div className={`${commonStyle.IconAytmPlace} ${style.helperIcon}`}>
            <i className="icon-aytmfd-exit" onClick={closeSimulation} />
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className={style.footer} data-testid="SW-EC-Footer">
      <div className={style.columns}>
        {COLUMNS.map((col, i) => (
          <div className={style.column} key={col.title}>
            <div className={style.columnTitle}>{t('questions', 'ec', 'kroger', col.title)}</div>
            {col.links.map(link => (
              <EmptyLink key={link} color="#0e51a1">
                <span className={style.columnLink}>{t('questions', 'ec', 'kroger', link)}</span>
              </EmptyLink>
            ))}
            {i === 0 && (
              <>
                <div className={style.getAppTitle}>{t('questions', 'ec', 'kroger', 'getTheApp')}</div>
                <div className={style.appBadges}>
                  <EmptyLink color="#0e51a1">
                    <img
                      src={appStoreBadge}
                      alt={t('questions', 'ec', 'kroger', 'appStoreBadge')}
                      className={style.appBadge}
                    />
                  </EmptyLink>
                  <EmptyLink color="#0e51a1">
                    <img
                      src={googlePlayBadge}
                      alt={t('questions', 'ec', 'kroger', 'googlePlayBadge')}
                      className={style.appBadge}
                    />
                  </EmptyLink>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className={style.bottomBar}>
        <img
          src={krogerLogoBlue}
          alt={t('questions', 'ec', 'kroger', 'store')}
          className={style.bottomLogo}
          onClick={onHomeClick}
        />
        <div className={style.bottomContent}>
          <div className={style.copyright}>{t('questions', 'ec', 'kroger', 'copyright')}</div>
          <div className={style.accessibility}>
            <EmptyLink color="#0e51a1">
              <span className={style.legalLink}>{t('questions', 'ec', 'kroger', 'accessibilityStatement')}</span>
            </EmptyLink>
            <span> {t('questions', 'ec', 'kroger', 'screenReaderNotice')} </span>
            <EmptyLink color="#0e51a1">
              <span className={style.legalLink}>{t('questions', 'ec', 'kroger', 'supportPhone')}</span>
            </EmptyLink>
          </div>
          <div className={style.legalLinks}>
            {LEGAL_LINKS.map(link => (
              <EmptyLink key={link} color="#0e51a1">
                <span className={style.legalLink}>{t('questions', 'ec', 'kroger', link)}</span>
              </EmptyLink>
            ))}
          </div>
        </div>
        <div className={style.social}>
          <div className={style.socialText}>{t('questions', 'ec', 'kroger', 'letsConnect')}</div>
          {SOCIAL.map(({ src, key }) => (
            <EmptyLink key={key} color="#0e51a1">
              <img src={src} alt={t('questions', 'ec', 'kroger', key)} className={style.socialIcon} />
            </EmptyLink>
          ))}
        </div>
      </div>
    </footer>
  )
}

export default Footer
